import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { UploadCloud, Trash2, Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mra_jwt";

const client = axios.create({ baseURL: API, withCredentials: true });
const readToken = () => localStorage.getItem(TOKEN_KEY);
client.interceptors.request.use((config) => {
  const t = readToken();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

/**
 * FileUpload — reusable auth-gated uploader.
 *
 * Props:
 *   purpose       — "campaign_cover" | "campaign_video" | "student_doc" | "misc"
 *   accept        — MIME accept string (defaults sensibly per purpose)
 *   maxBytes      — client-side pre-check (server enforces final limit)
 *   value         — existing file id (renders preview instead of dropzone)
 *   onChange({ id, url, ... }) — fires after successful upload
 *   label, hint   — UI copy
 */
const PURPOSE_DEFAULTS = {
  campaign_cover: {
    accept: "image/jpeg,image/png,image/webp",
    maxBytes: 8 * 1024 * 1024,
    label: "Cover image",
    hint: "JPG, PNG or WebP up to 8 MB.",
    kind: "image",
  },
  campaign_video: {
    accept: "video/mp4,video/webm,video/quicktime",
    maxBytes: 120 * 1024 * 1024,
    label: "Intro video",
    hint: "MP4, WebM or MOV up to 120 MB.",
    kind: "video",
  },
  student_doc: {
    accept: "application/pdf,image/jpeg,image/png",
    maxBytes: 15 * 1024 * 1024,
    label: "Document",
    hint: "PDF or image, up to 15 MB.",
    kind: "doc",
  },
  misc: {
    accept: "image/*,application/pdf",
    maxBytes: 5 * 1024 * 1024,
    label: "File",
    hint: "Image or PDF up to 5 MB.",
    kind: "file",
  },
};

const bytesFmt = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const authedSrc = (url) => {
  // Server returns url like `/api/files/{id}?auth=<jwt>` — prepend backend host
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

export const FileUpload = ({
  purpose = "misc",
  accept,
  maxBytes,
  value,          // existing UploadResponse-shaped record
  onChange,
  label,
  hint,
  className = "",
  testid = "file-upload",
  campaignId = null,
}) => {
  const defaults = PURPOSE_DEFAULTS[purpose];
  const effAccept = accept ?? defaults.accept;
  const effMax = maxBytes ?? defaults.maxBytes;
  const effLabel = label ?? defaults.label;
  const effHint = hint ?? defaults.hint;
  const kind = defaults.kind;

  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [record, setRecord] = useState(value || null);

  useEffect(() => {
    if (value && value.id !== record?.id) setRecord(value);
  }, [value, record]);

  const doUpload = useCallback(async (file) => {
    setError("");
    if (file.size > effMax) {
      setError(`File is ${bytesFmt(file.size)} — max ${bytesFmt(effMax)}.`);
      return;
    }
    setUploading(true);
    setProgress(0);
    const form = new FormData();
    form.append("purpose", purpose);
    if (campaignId) form.append("campaign_id", campaignId);
    form.append("file", file);
    try {
      const res = await client.post("/uploads", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      setRecord(res.data);
      onChange?.(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed. Try again in a moment.");
    } finally {
      setUploading(false);
    }
  }, [effMax, onChange, purpose, campaignId]);

  const onFileInput = (e) => {
    const f = e.target.files?.[0];
    if (f) doUpload(f);
    e.target.value = ""; // allow re-selecting the same file
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) doUpload(f);
  };

  const clear = async () => {
    if (!record) return;
    // Fire and forget soft-delete; ignore errors so UX stays fast
    try { await client.delete(`/files/${record.id}`); } catch { /* ignore */ }
    setRecord(null);
    onChange?.(null);
  };

  return (
    <div className={className} data-testid={testid}>
      <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">{effLabel}</p>

      <AnimatePresence mode="wait">
        {record ? (
          <motion.div
            key="preview"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-2 flex items-start gap-4 rounded-xl border border-border bg-bone p-4"
            data-testid={`${testid}-preview`}
          >
            {kind === "image" && (
              <img
                src={authedSrc(record.url)}
                alt={record.original_filename || "Uploaded file"}
                className="h-24 w-24 rounded-md object-cover"
                data-testid={`${testid}-image`}
              />
            )}
            {kind === "video" && (
              <video
                src={authedSrc(record.url)}
                controls
                className="h-32 w-56 rounded-md bg-ink object-cover"
                data-testid={`${testid}-video`}
              />
            )}
            {kind === "doc" && (
              <a
                href={authedSrc(record.url)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-md border border-border bg-sand px-3 py-2 text-base font-medium text-ink hover:border-ink"
                data-testid={`${testid}-doc`}
              >
                View {record.original_filename || "file"}
              </a>
            )}
            <div className="flex-1">
              <p className="font-serif text-3xl text-ink">{record.original_filename || "Uploaded file"}</p>
              <p className="mt-0.5 text-[13px] text-olive">
                {record.content_type} · {bytesFmt(record.size || 0)}
              </p>
              <button
                type="button"
                onClick={clear}
                data-testid={`${testid}-clear`}
                className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-semibold text-scarlet hover:underline"
              >
                <Trash2 className="h-3 w-3" /> Remove
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="drop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            className={`mt-2 flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
              dragging ? "border-ink bg-ink/[0.03]" : "border-border bg-sand"
            }`}
          >
            <UploadCloud className={`h-8 w-8 ${dragging ? "text-ink" : "text-olive"}`} strokeWidth={1.5} />
            <p className="mt-3 font-serif text-3xl text-ink">
              Drop it here — or{" "}
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                data-testid={`${testid}-browse`}
                className="underline underline-offset-4 hover:text-tangerine"
              >
                pick a file
              </button>
            </p>
            <p className="mt-1 text-[13px] text-olive">{effHint}</p>

            {uploading && (
              <div className="mt-4 w-full max-w-xs" data-testid={`${testid}-progress`}>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-border">
                  <div
                    className="h-full bg-ink transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="mt-1 flex items-center justify-center gap-1.5 text-[13px] text-olive">
                  <Loader2 className="h-3 w-3 animate-spin" /> Uploading… {progress}%
                </p>
              </div>
            )}

            <input
              ref={inputRef}
              type="file"
              accept={effAccept}
              onChange={onFileInput}
              className="hidden"
              data-testid={`${testid}-input`}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <p role="alert" data-testid={`${testid}-error`} className="mt-2 flex items-center gap-1.5 text-[13px] text-scarlet">
          <X className="h-3.5 w-3.5" /> {error}
        </p>
      )}
    </div>
  );
};
